import { useMsal } from "@azure/msal-react";
import {
    CommandBar, IButtonProps,
    ICommandBarItemProps, IContextualMenuItem, MessageBar, MessageBarType, useTheme, Text
} from "@fluentui/react";
import { RootState } from "app/store";
import saveAs from "file-saver";
import BreadcrumbNavigation from "global/Application/BreadcrumbNavigation";
import { useApplication } from "global/Application/Context";
import { useAuthentication } from "global/Authentication";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import { addUpdateGroupConfigurationShowInActives, notificationRemove } from "global/Reducer/dataSlice";
import { IComponentContextMenu } from "model/Application";
import * as IXrmOptions from "model/AttributeOptionSets.type";
import * as FAM from "model/FlexibleAttributeModel";
import { IGLWindow } from "model/GroupLibraryWindow";
import { useEffect, useMemo, useRef, useState } from "react";
import { ConnectedProps, connect, useDispatch } from 'react-redux';
import { useHistory } from "react-router";
import { EViewType, emitCustomEvent, fadeIn } from "../../../global/Application";
import { ESaveType, SaveAction, SaveAsync, loadSnapshotAction } from '../../../global/Application/dataActions';
import {
    ELoadStatus,
    setConcurrencyConfirmationModal, setIsNavPinned, setProcessing, setViewType, updateDebug
} from "../../../global/Reducer/statusSlice";
import CommentPanel from "../../../views/BlueRelationship/CommentPanel";
import { ConfirmationDialog } from "../../controls/ConfirmationDialog";


const overflowProps: IButtonProps = { ariaLabel: 'More commands' };


interface IEditorNavbarProps extends TComponentConnectedProps, IComponentContextMenu {
    hideShowDeactivated?: boolean;
}

const EditorNavbar = (props: IEditorNavbarProps) => {
    const history = useHistory();

    const authentication = useAuthentication();
    const appContext = useApplication();

    const [commentsPanel, setCommentsPanel] = useState(false);

    const dispatch = useDispatch();
    const theme = useTheme();

    const refStickyBar = useRef(null);
    const [sticky, setSticky] = useState(false);

    const isCheckList = props.state.router.location.pathname.indexOf('/checklist') > -1;

    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const canModify = props.state?.status?.viewType === EViewType.Modify;

    let menuOverflow: ICommandBarItemProps[] = [];
    const menuFar: ICommandBarItemProps[] = [];

    let menu: ICommandBarItemProps[] = [];
    const subMenuSave: IContextualMenuItem[] = [];

    let isConfirm = false;

    let submitAsUnderWritingReviewComplete = false;
    if (
        props.state.data.json.changeType?.value === IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode["PSA Change(808900012)"].value ||
        props.state.data.json.changeType?.value === IXrmOptions.bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Administrative Change(808900003)"].value
    ) {
        submitAsUnderWritingReviewComplete = true;
    }

    function onScroll(_: any) {
        if (!sticky && window.pageYOffset >= 50 && props.state.status.isNavPinned)
            // if (!isCheckList)
            setSticky(true);

        if (sticky && window.pageYOffset < 50)
            setSticky(false);
    };

    useEffect(() => {
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, [sticky, props.state.status.isNavPinned]);

    useEffect(() => {
        if (!props.state.status.isNavPinned)
            setSticky(false);
    }, [props.state.status.isNavPinned]);

    useEffect(() => {
        onScroll(null);
    }, []);

    const styleStickyBar = useMemo(() => {
        if (!sticky)
            return {
                transition: '0.1s',
                backgroundColor: theme.semanticColors.bodyBackground,
            } as React.CSSProperties;

        const x: React.CSSProperties = {
            position: 'fixed',
            top: '0',
            width: '100%',
            zIndex: 100000,
            borderBottom: '1px solid gray',
            transition: '0.1s',
            backgroundColor: theme.semanticColors.bodyBackground,
        };

        return x;
    }, [sticky, theme]);

    // For Admin-Change/PSA-Change we ought to display 'Confirm'
    // Updates to Submit Process for Admin Changes & PSA Changes
    // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/250052
    isConfirm = isConfirm || submitAsUnderWritingReviewComplete;

    // For cancellation we ought to display 'Confirm'
    isConfirm = isConfirm || props.state?.data?.json?.status?.value === FAM.EGroupConfigurationStatus['Transferred to Other LOB in Progress'] ||
        props.state?.data?.json?.status?.value === FAM.EGroupConfigurationStatus['Cancellation in Progress'];

    menuFar.push({
        key: 'share',
        text: 'Share',
        cacheKey: 'myCacheKey', // changing this key will invalidate this item's cache
        iconProps: { iconName: 'Share' },
        subMenuProps: {
            items: [
                {
                    key: 'emailMessage',
                    text: 'Email Link',
                    iconProps: { iconName: 'Mail' },
                    onClick: () => {
                        const subject = `Group Library - ${props.state.data.snapshot.bcbs_name}`;
                        const body = `Regarding ${window.location.href}\r\n`;
                        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
                    }
                },
                {
                    key: 'calendarEvent',
                    text: 'Copy Page Link',
                    iconProps: { iconName: 'PageLink' },
                    onClick: () => {
                        navigator.clipboard.writeText(window.location.href);
                    }
                },
            ],
        },
    },
        {
            key: 'pin',
            text: '',
            iconProps: { iconName: props.state.status.isNavPinned ? 'PinnedSolid' : 'Pin' },
            onClick: (event, item) => {
                dispatch(setIsNavPinned());
            }
        }

    );

    menu.push({
        key: 'back',
        text: 'Back',
        iconProps: { iconName: 'Back' },
        onClick: () => {
            history.goBack();
        }
    });

    if (canModify && !isCheckList) {
        subMenuSave.push({
            key: 'saveSnapshot',
            text: 'Save',
            iconProps: { iconName: 'BrowserTabScreenshot' },
            onClick: () => {
                appContext.SaveAsync({
                    saveType: ESaveType.EManual,
                });
            },
        });

        if (props.state.data.config.bcbs_config_release?.statuscode !== IXrmOptions.bcbs_config_release.statuscode["Published(2)"].value) {
            subMenuSave.push({
                key: 'updateRelease',
                text: 'Update Release',
                iconProps: { iconName: 'Sync' },
                onClick: () => {
                    dispatch(
                        SaveAction(
                            dataSource,
                            history,
                            {
                                saveType: ESaveType.EUpgradeRelease,
                                onSaveStatusScreen: true,
                            }
                        )
                    );
                },
            });
        }

        subMenuSave.push({
            key: 'submitSnapshot',
            text: isConfirm
                ? 'Confirm'
                : 'Submit',
            iconProps: { iconName: 'MedalSolid' },
            onClick: (event, item) => {
                appContext.SaveAsync({
                    saveType: ESaveType.ESubmit
                });
            }
        });

        if (props.state.status.debug.admin)
            subMenuSave.push({
                key: 'submitByPassSnapshot',
                text: 'Confirm - Bypass Underwriting and Task Generation',
                iconProps: { iconName: 'MedalSolid' },
                onClick: (event, item) => {
                    appContext.SaveAsync({
                        saveType: ESaveType.ESubmit,
                        onSaveBypassUnderwritingTaskGeneration: true,
                    });
                }
            });

        menu.push({
            key: 'save',
            text: 'Save',
            iconProps: { iconName: 'Save' },
            disabled: props.state.status.dataSavingStatus === ELoadStatus.criticalSaving,
            subMenuProps: {
                items: subMenuSave
            }
        });
    }

    if (props?.contextMenu?.items)
        menu.push(...props.contextMenu.items);

    if (props.state.status.debug.admin ||
        (authentication.authorizations?.canEdit && props.state.data.snapshot?.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Corrections Pending(808900005)'].value)
    )
        menu.push({
            key: 'comments',
            text: 'View Comments',
            iconProps: { iconName: 'Comment' },
            cacheKey: 'myCacheKey',
            onClick: () => { setCommentsPanel(!commentsPanel) }
        });

    if (!isCheckList && !props.hideShowDeactivated)
        menu.push({
            key: 'showDeactivated',
            text: 'Show Deactivated',
            iconProps: {
                iconName: props.state?.data?.json?.showInActives?.value ? 'RedEye' : 'Hide',
                styles: {
                }
            },
            disabled: props.state.status.dataSavingStatus === ELoadStatus.saving,
            buttonStyles: {
                root: {
                    backgroundColor: !props?.state?.data?.json?.showInActives?.value ? undefined : theme.semanticColors.warningBackground,
                }
            },
            onClick: () => {
                dispatch(addUpdateGroupConfigurationShowInActives(
                    {
                        type: 'bool',
                        value: !props?.state?.data?.json?.showInActives?.value
                    }
                ));

            }
        });

    let myLastSaveDate: Date = new Date();
    let lastSaveDate: Date = new Date();

    if (props.state?.status?.lastSaved) {
        myLastSaveDate = new Date(props.state?.status?.lastSaved);
    } else if (props.state?.data?.snapshot?.modifiedon) {
        myLastSaveDate = new Date(props.state?.data?.snapshot?.modifiedon);
    }

    if (props.state.status.concurrencyConfirmationModal?.modifiedon) {
        lastSaveDate = new Date(props.state.status.concurrencyConfirmationModal?.modifiedon);
    }

    const adminMenu = () => {
        const debugSubMenu: IContextualMenuItem[] = [];

        debugSubMenu.push({
            key: 'debugViewMode',
            text: props.state.status.viewType === EViewType.ViewOnly
                ? '✏️ Switch to Edit'
                : '👁️ Switch to View',
            onClick: (event, item) => {
                dispatch(updateDebug({ autoSaveOff: true }));

                if (props.state.status.viewType === EViewType.ViewOnly)
                    dispatch(setViewType(EViewType.Modify));

                else
                    dispatch(setViewType(EViewType.ViewOnly));

            }
        });


        debugSubMenu.push({
            key: 'liveload',
            text: props.state.status.debug.liveload
                ? '🟢 Reload with live configuration'
                : '⚫ Reload with live configuration',
            onClick: (event, item) => {
                dispatch(updateDebug({ liveload: true }));
                dispatch(updateDebug({ autoSaveOff: true }));
                dispatch(loadSnapshotAction(
                    dataSource,
                    props.state.data.account?.accountid!,
                    {
                        primarySnapshotId: props.state.data.snapshot.bcbs_groupconfigurationsnapshotid,
                    }
                ));
            }
        });

        debugSubMenu.push({
            key: 'diagnosis',
            text: props.state.status.debug.diagnosis
                ? '🟢 Diagnosis'
                : '⚫ Diagnosis',
            onClick: (event, item) => {
                dispatch(updateDebug({ diagnosis: !props.state.status.debug.diagnosis }));
                dispatch(updateDebug({ autoSaveOff: true }));
            }
        });



        debugSubMenu.push({
            key: 'debugAdminOverride',
            text: props.state.status.debug.admin
                ? '🟢 Admin override'
                : '⚫ Admin override',
            onClick: (event, item) => {
                dispatch(updateDebug({ admin: !props.state.status.debug.admin }));
                dispatch(updateDebug({ autoSaveOff: true }));
            }
        });


        debugSubMenu.push({
            key: 'debugAutoSaveOff',
            text: props.state.status.debug.autoSaveOff
                ? '🟢 Autosave is off'
                : '⚫ Autosave is on',
            onClick: (event, item) => {
                dispatch(updateDebug({ autoSaveOff: !props.state.status.debug.autoSaveOff }));
            }
        });

        debugSubMenu.push({
            key: 'debugDownloadDataModel',
            text: '🌐 Download Data Model',
            onClick: (event, item) => {
                (async () => {
                    try {

                        dispatch(setProcessing(true));
                        const response = await dataSource.getGenerateDataDictionaryExtract();
                        saveAs(response.blob, response.fileName);

                    } catch (error) {
                        // lets create a new one is payload is not providing one
                        emitCustomEvent({
                            eventType: 'globalException',
                            details: error
                        });

                    } finally {
                        dispatch(setProcessing(false));
                    }
                })();
            }
        });

        return ({
            key: 'Admin',
            text: 'Admin',
            iconProps: { iconName: 'Settings' },
            disabled: props.state.status.dataSavingStatus === ELoadStatus.criticalSaving,
            subMenuProps: {
                items: debugSubMenu
            }
        } as ICommandBarItemProps);
    }

    const onRenderNotificationBar = () => {
        const elements: JSX.Element[] = [];

        if (props.state.data.json.showInActives?.value)
            elements.push(
                <MessageBar key={"notification.showInActives"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Showing only deactivated records. All records below are currently deactivated.
                </MessageBar>
            )

        if (props.state.status.debug.liveload)
            elements.push(
                <MessageBar key={"notification.liveload"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    You are currently live loading the configuration.
                </MessageBar>
            )

        if (props.state.status.debug.admin)
            elements.push(
                <MessageBar key={"notification.admin"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Admin mode has been enabled.
                </MessageBar>
            )

        if (props.state.status.debug.developer)
            elements.push(
                <MessageBar key={"notification.admin"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Developer mode has been enabled.
                </MessageBar>
            )

        if (props.state.status.debug.noVisibility)
            elements.push(
                <MessageBar key={"notification.noVisibility"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Visibility loading has been disabled.
                </MessageBar>
            )

        if (props.state.data.json.MNIL?.value)
            elements.push(
                <MessageBar key={"notification.MNIL"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Showing fields for MNIL.
                </MessageBar>
            )

        if (((window as IGLWindow).preview))
            elements.push(
                <MessageBar key={"notification.preview"} className={fadeIn} messageBarType={MessageBarType.warning}>
                    Preview Mode has been enabled
                </MessageBar>
            )

        props.state.data.notifications?.forEach((notification, index) => {
            elements.push(
                <MessageBar
                    key={notification.id}
                    className={fadeIn}
                    messageBarType={MessageBarType.warning}
                    onDismiss={(ev) => {
                        dispatch(notificationRemove(notification.id));
                    }}
                    dismissButtonAriaLabel="Close"
                >
                    <Text style={{ whiteSpace: 'pre-line' }}>
                        {notification.description}
                    </Text>
                </MessageBar>
            )
        });

        if (elements.length > 0)
            return <div key={"notification.host"} style={{ borderTop: '1px solid gray' }}>
                {elements}
            </div>;
        else
            return <></>;

    }

    if (authentication.authorizations?.roles['BCBS - Power User']) {
        menu.push(adminMenu());
    }

    return <>
        <div
            style={{
                height: sticky ? `${(refStickyBar?.current as any)?.offsetHeight}px` ?? '100px' : '0px',
                width: '100%',
            }}
        >
        </div>

        <div style={styleStickyBar} ref={refStickyBar}>
            <CommandBar
                key={"commandBar"}
                styles={{ root: { backgroundColor: theme.palette.white, position: 'sticky', top: '10px' } }}
                items={menu}
                overflowItems={menuOverflow}
                overflowButtonProps={overflowProps}
                farItems={menuFar}
            />
            <div key={"breadcrumb"} style={{ paddingLeft: '44px', paddingRight: '44px' }}>
                <BreadcrumbNavigation />
            </div>

            {onRenderNotificationBar()}
        </div>

        {
            /* TODO move outside of navbar and refactor comments panel */
            props.state.data.snapshot?.statuscode === IXrmOptions.bcbs_groupconfigurationsnapshot.statuscode['Corrections Pending(808900005)'].value &&
            <CommentPanel
                isOpen={commentsPanel}
                dismissPanel={() => { setCommentsPanel(!commentsPanel) }}
            />
        }

        {props.state.status.concurrencyConfirmationModal &&
            <ConfirmationDialog
                onClose={() => {
                    dispatch(setConcurrencyConfirmationModal(null));
                    dispatch(setViewType(EViewType.ViewOnly));
                }}
                onContinue={async () => {
                    //todo Armen:  make new snapshot from current state data store, keep original parent snapshot

                    // TODO WARN* there is a duplicate of this function, that must be refactored
                    dispatch(setConcurrencyConfirmationModal(null));
                    const t = await SaveAsync(dispatch, dataSource, props.state, { saveType: ESaveType.EConcurrencyConflict, onSaveStatusScreen: true });

                    history.push('/finished');
                    window.open(`/records/blueRelationship/${t.snapshot?._bcbs_groupid_value}/groupConfiguration/${t.snapshot?.bcbs_groupconfigurationsnapshotid}`);

                    //not sure about the behavior we want to have but save has dispatch to update status.snapshot with latest
                    //but the function may complete prior to dispatch cycle
                }}
                continueText="Save current configuration as a new configuration"
                cancelText="View your current changes in read-only mode"
            >
                <p>
                    Your version of the configuration is out of date so we cannot save your changes. The changes in this tab were last saved at {myLastSaveDate.toLocaleString()}, but {props.state.status.concurrencyConfirmationModal?.fullname} modified this record at {lastSaveDate.toLocaleString()}.
                    Please choose whether to create a new configuration from your changes, or view your current changes in read-only mode.
                </p>
            </ConfirmationDialog>
        }
    </>
};

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connect(mapStateToProps)(EditorNavbar)